import Badge, { BadgeVariant } from "@/components/custom/badge";
import Card from "@/components/custom/card";
import FilePreview from "@/components/custom/file-preview";
import { currencyFormatter, formatDate } from "@/lib/utils";
import { CaretLeft } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetTransactionById } from "@/services/queries/transactions";
import Loading from "@/components/custom/loading";
import { toast } from "@/hooks/use-toast";
import { useGetCurrencies } from "@/services/queries/currency";
import { useGetAccountById } from "@/services/queries/accounts";
import {
  Account,
  ApiResponse,
  Attachment,
  Budget,
  BudgetCategory,
  BudgetSubCategory,
  Expense,
  TransactionLineItem,
  Vendor,
  Vessel,
} from "@/lib/types";
import { Currency, Transaction } from "@/lib/types";
import { TFunction } from "i18next";
import { ACCOUNT_TYPES, TRANSACTION_STATUS } from "@/lib/constants";
import { useGetVendorById } from "@/services/queries/vendor";
import {
  useGetBudgetById,
  useGetBudgetCategoryById,
  useGetBudgetSubCategoryById,
} from "@/services/queries/budgets";
import { useGetVesselById } from "@/services/queries/vessel";
import { api, BASE_URL } from "@/lib/api";
import EditExpenseForm from "../components/edit-expense-form";
import { Mixpanel } from "@/lib/mixpanel";
import TransferForm from "../components/transfer-form";
import DeleteTransactionDialog from "../components/delete-transaction-dialog";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";

const TransactionDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const {
    data: transactionDetailData,
    isPending,
    isError,
    refetch,
    canonicalId,
    isCanonical,
  } = useGetTransactionById(id ?? "", {
    enabled: !!id,
  });
  const [openEditExpenseForm, setOpenEditExpenseForm] = useState(false);
  const [openEditTransferForm, setOpenEditTransferForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const canWriteExpenses = useHasPermission(PERMISSIONS.Expenses.Write);

  const transaction = useMemo(
    () => transactionDetailData?.value,
    [transactionDetailData]
  );

  const { data: currenciesData } = useGetCurrencies();
  const { data: accountData } = useGetAccountById(
    transaction && "fromAccountId" in transaction
      ? transaction.fromAccountId
      : undefined
  );
  const { data: vendorData } = useGetVendorById(
    transaction && "vendorId" in transaction ? transaction.vendorId : undefined
  );
  const { data: fromAccountData } = useGetAccountById(
    transaction && "fromAccountId" in transaction
      ? transaction.fromAccountId
      : undefined
  );
  const { data: toAccountData } = useGetAccountById(
    transaction && "toAccountId" in transaction
      ? transaction.toAccountId
      : undefined
  );
  const { data: budgetData } = useGetBudgetById(
    transaction && "budgetId" in transaction ? transaction.budgetId : undefined
  );
  const { data: budgetCategoryData } = useGetBudgetCategoryById(
    {
      id:
        transaction && "budgetCategoryId" in transaction
          ? transaction.budgetCategoryId || ""
          : "",
    },
    {
      enabled: !!(
        transaction &&
        "budgetCategoryId" in transaction &&
        transaction.budgetCategoryId
      ),
    }
  );
  const { data: budgetSubcategoryData } = useGetBudgetSubCategoryById(
    {
      id:
        transaction && "budgetSubcategoryId" in transaction
          ? transaction.budgetSubcategoryId || ""
          : "",
    },
    {
      enabled: !!(
        transaction &&
        "budgetSubcategoryId" in transaction &&
        transaction.budgetSubcategoryId
      ),
    }
  );
  const { data: vesselData } = useGetVesselById(
    transaction && "vesselId" in transaction ? transaction.vesselId : undefined
  );

  const currencies = useMemo(() => currenciesData?.value, [currenciesData]);
  const currency = useMemo(() => {
    if (transaction && "currencyId" in transaction) {
      return currencies?.find(
        (currency) => currency.id === transaction?.currencyId
      );
    }
  }, [currencies, transaction]);

  const convertedCurrency = useMemo(() => {
    if (transaction && "convertedCurrencyId" in transaction) {
      return currencies?.find(
        (currency) => currency.id === transaction?.convertedCurrencyId
      );
    }
  }, [currencies, transaction]);

  const account = useMemo(() => accountData?.value, [accountData]);
  const vendor = useMemo(() => vendorData?.value, [vendorData]);
  const fromAccount = useMemo(() => fromAccountData?.value, [fromAccountData]);
  const toAccount = useMemo(() => toAccountData?.value, [toAccountData]);
  const budget = useMemo(() => budgetData?.value, [budgetData]);
  const budgetCategory = useMemo(
    () => budgetCategoryData?.value,
    [budgetCategoryData]
  );
  const budgetSubcategory = useMemo(
    () => budgetSubcategoryData?.value,
    [budgetSubcategoryData]
  );
  const vessel = useMemo(() => vesselData?.value, [vesselData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // BE may return the parent when a child id is requested. Replace the route
  // to the parent's canonical URL before binding the view (and the embedded
  // edit modals) to the payload.
  useEffect(() => {
    if (id && canonicalId && !isCanonical) {
      navigate(`/transaction/${canonicalId}`, { replace: true });
    }
  }, [id, canonicalId, isCanonical, navigate]);

  useEffect(() => {
    if (isError) {
      navigate(
        location.state?.redirectPath ? "/" + location.state?.redirectPath : "/"
      );
      toast({
        title: t("error_message.expense_not_found"),
        description: t("error_message.expense_not_found_description"),
        variant: "destructive",
      });
    }
  }, [isError, location.state?.redirectPath, navigate, t]);

  return (
    <>
      <title>{t("title.expense_details")}</title>

      <div data-testid="transaction-detail-page" className="px-5">
        <div className="flex items-center justify-between">
          <button
            aria-label="back button"
            onClick={() => {
              navigate(
                location.state?.redirectPath
                  ? "/" + location.state?.redirectPath
                  : "/"
              );

              Mixpanel.track("Back Button Clicked");
            }}
          >
            <CaretLeft size={20} color="#FFFFFF" />
          </button>
          {canWriteExpenses &&
            transaction &&
            isCanonical &&
            (transaction.type === "Expense" ||
              transaction.type === "Transfer") && (
              <button
                className="text-[15px] font-medium text-[#45D3EC]"
                onClick={() =>
                  transaction.type === "Expense"
                    ? setOpenEditExpenseForm(true)
                    : setOpenEditTransferForm(true)
                }
              >
                {t("button.edit")}
              </button>
            )}
        </div>

        {transaction && isCanonical && transaction.type === "Expense" && (
          <EditExpenseForm
            open={openEditExpenseForm}
            setOpen={setOpenEditExpenseForm}
            expense={transaction as Expense}
            refetch={refetch}
          />
        )}

        {transaction && isCanonical && transaction.type === "Transfer" && (
          <TransferForm
            open={openEditTransferForm}
            setOpen={setOpenEditTransferForm}
            defaultValues={transaction}
            title={t("title.edit_transfer")}
            refetch={refetch}
          />
        )}

        <DeleteTransactionDialog
          visible={openDeleteDialog}
          setVisible={setOpenDeleteDialog}
          id={transaction?.id}
          refetch={refetch}
          onSuccess={() => {
            navigate(
              location.state?.redirectPath
                ? "/" + location.state?.redirectPath
                : "/"
            );
          }}
        />

        {isPending || !transaction || !isCanonical ? (
          <Loading className="mt-10" />
        ) : isError ? (
          <div className="mt-10">
            <h1 className="text-center text-2xl font-medium text-white">
              {t("error_message.expense_not_found")}
            </h1>
          </div>
        ) : (
          <>
            {renderHeader(currency, convertedCurrency, transaction, t)}

            <div className="mt-10 w-full space-y-[15px]">
              {renderCostCenter(
                "vesselName" in transaction
                  ? transaction.vesselName
                  : undefined,
                t
              )}
              {renderFromAccount(fromAccount, t)}
              {renderToAccount(toAccount, t)}
              {renderVessel(vessel, t)}
              {renderPaymentMethod(account, t)}
              {renderBudget(budget, t)}
              {renderBudgetCategory(budgetCategory, budgetSubcategory, t)}
              {renderTransactionDate(transaction.transactionDate, t)}
              {renderInvoiceDate(
                "invoiceDate" in transaction
                  ? transaction.invoiceDate
                  : undefined,
                t
              )}
              {renderDueDate(
                "dueDate" in transaction ? transaction.dueDate : undefined,
                t
              )}
              {renderVendor(vendor, t)}
              <FilesPreview
                files={
                  "attachments" in transaction && transaction.attachments
                    ? transaction.attachments
                    : []
                }
              />
              {renderNote(transaction.description, t)}
              {renderLineItems(
                "lineItems" in transaction
                  ? (transaction.lineItems as TransactionLineItem[])
                  : [],
                t,
                currency
              )}
            </div>

            {canWriteExpenses && (
              <div className="mt-10 flex justify-center pb-6">
                <button
                  data-testid="delete-transaction-button"
                  className="text-[15px] font-medium text-destructive"
                  onClick={() => setOpenDeleteDialog(true)}
                >
                  {t("button.delete_transaction")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

const renderHeader = (
  currency: Currency | undefined,
  convertedCurrency: Currency | undefined,
  transaction: Transaction,
  t: TFunction
) => {
  const transactionStatus = TRANSACTION_STATUS.find(
    (status) => status.value === transaction.status
  );

  return (
    <div className="mt-10 space-y-5 px-3">
      <div className="flex flex-col gap-1">
        <span className="text-xl text-white/80">
          {t(`transaction_type.${transaction.type.toLowerCase()}`)}
        </span>
        <span className="text-[40px] font-semibold leading-[44px] text-white">
          {currencyFormatter(transaction.amount, currency?.code)}
        </span>
        {"convertedAmount" in transaction &&
        "conversionRate" in transaction &&
        transaction.convertedAmount &&
        transaction.conversionRate &&
        convertedCurrency ? (
          <div className="flex items-center gap-1">
            <span className="text-lg font-medium text-white/60">
              {currencyFormatter(
                transaction.convertedAmount,
                convertedCurrency?.code
              )}
            </span>
            <span className="text-sm text-white/40">
              ({t("label.rate")}: {transaction.conversionRate.toFixed(6)})
            </span>
          </div>
        ) : null}
      </div>

      {transaction.status && (
        <Badge
          label={t(`transaction_status.${transactionStatus?.translationKey}`)}
          variant={transactionStatus?.variant as BadgeVariant}
          icon={transactionStatus?.icon}
        />
      )}
    </div>
  );
};

const renderCostCenter = (name: string | undefined, t: TFunction) => {
  if (!name) return null;

  return (
    <Card className="h-[66px] py-[14px]">
      <div className="flex flex-col items-start justify-between">
        <span className="text-xs leading-4 text-white/50">
          {t("label.cost_center")}
        </span>
        <p className="text-[15px] text-white">{name}</p>
      </div>
    </Card>
  );
};

const renderPaymentMethod = (account: Account | undefined, t: TFunction) => {
  if (!account) return null;

  const accountType = ACCOUNT_TYPES.find(
    (type) => type.label === account?.type
  );

  return (
    <Card className="h-[66px] py-[14px]">
      <div className="flex flex-col items-start justify-between">
        <span className="text-xs leading-4 text-white/50">
          {t("label.payment_method")}
        </span>
        <p className="flex items-center gap-1 text-[15px] text-white">
          {accountType && (
            <>
              <span>{t(`account_type.${accountType?.translationKey}`)}</span>
              <span>/</span>
            </>
          )}

          <span>{account?.accountNumber}</span>
        </p>
      </div>
    </Card>
  );
};

const renderTransactionDate = (
  date: Date | string | undefined,
  t: TFunction
) => {
  if (!date) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">{t("label.date")}</span>
      <span className="text-[15px] text-white">
        {formatDate(new Date(date), "dd MMMM yyyy - HH:mm")}
      </span>
    </Card>
  );
};

const renderDueDate = (date: Date | string | undefined, t: TFunction) => {
  if (!date) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.due_date")}
      </span>
      <span className="text-[15px] text-white">
        {formatDate(new Date(date), "dd MMMM yyyy - HH:mm")}
      </span>
    </Card>
  );
};

const renderInvoiceDate = (date: Date | string | undefined, t: TFunction) => {
  if (!date) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.invoice_date")}
      </span>
      <span className="text-[15px] text-white">
        {formatDate(new Date(date), "dd MMMM yyyy - HH:mm")}
      </span>
    </Card>
  );
};

const FilesPreview = ({ files }: { files: Attachment[] }) => {
  const { t } = useTranslation();
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    const getPreviewUrls = async () => {
      if (!files || files.length === 0) {
        setUrls([]);
        return;
      }

      try {
        const urlResults = await Promise.all(
          files.map(async (file) => {
            const res = await api.get<ApiResponse<string>>(
              `${BASE_URL}/transactions/attachments/${file.id}/url`
            );
            return res.data;
          })
        );
        setUrls(urlResults.map((result) => result.value));
      } catch (error) {
        console.error("Error fetching attachment URLs:", error);
        setUrls([]);
      }
    };

    getPreviewUrls();
  }, [files]);

  if (!files || files.length === 0) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.files")}
      </span>
      <FilePreview className="mt-1" urls={urls} />
    </Card>
  );
};

const renderNote = (note: string | undefined, t: TFunction) => {
  if (!note || note === "") return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">{t("label.note")}</span>
      <p className="text-[15px] text-white">{note}</p>
    </Card>
  );
};

const renderVendor = (vendor: Vendor | undefined, t: TFunction) => {
  if (!vendor) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.vendor")}
      </span>
      <p className="text-[15px] text-white">{vendor.name}</p>
    </Card>
  );
};

const renderFromAccount = (account: Account | undefined, t: TFunction) => {
  if (!account) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.from_account")}
      </span>
      <p className="text-[15px] text-white">{account.name}</p>
    </Card>
  );
};

const renderToAccount = (account: Account | undefined, t: TFunction) => {
  if (!account) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.to_account")}
      </span>
      <p className="text-[15px] text-white">{account.name}</p>
    </Card>
  );
};

const renderBudget = (budget: Budget | undefined, t: TFunction) => {
  if (!budget) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.budget")}
      </span>
      <p className="text-[15px] text-white">{budget.name}</p>
    </Card>
  );
};

const renderBudgetCategory = (
  budgetCategory: BudgetCategory | undefined,
  budgetSubcategory: BudgetSubCategory | undefined,
  t: TFunction
) => {
  if (!budgetCategory) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.budget_category")}
      </span>
      <p className="text-[15px] text-white">
        {budgetCategory.name}
        {budgetSubcategory && ` / ${budgetSubcategory.name}`}
      </p>
    </Card>
  );
};

const renderVessel = (vessel: Vessel | undefined, t: TFunction) => {
  if (!vessel) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.vessel")}
      </span>
      <p className="text-[15px] text-white">{vessel.name}</p>
    </Card>
  );
};

const renderLineItems = (
  lineItems: TransactionLineItem[] | undefined,
  t: TFunction,
  currency: Currency | undefined
) => {
  if (!lineItems || lineItems.length === 0) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="mb-2 text-xs leading-4 text-white/50">
        {t("label.items")}
      </span>

      <div className="flex w-full flex-col gap-2">
        {lineItems.map((lineItem) => (
          <div
            key={lineItem.id}
            data-testid={`line-item-${lineItem.id}`}
            className="w-full border-b border-white/10 py-3 first:pt-0 last:border-b-0 last:pb-0"
          >
            <p className="mb-2 text-[15px] font-medium text-white">
              {lineItem.description}
            </p>
            <div className="flex flex-wrap gap-10 gap-y-1 text-sm sm:gap-x-20">
              <div className="flex flex-col gap-1">
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.amount")}:
                  </span>
                  {currencyFormatter(lineItem.amount, currency?.code)}
                </p>
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.quantity")}:
                  </span>
                  {lineItem.quantity}
                </p>
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.unit_price")}:
                  </span>
                  {currencyFormatter(lineItem.unitPrice, currency?.code)}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.vat_rate")}:
                  </span>
                  {lineItem.vatRate}%
                </p>
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.vat_amount")}:
                  </span>
                  {currencyFormatter(
                    lineItem.quantity *
                      lineItem.unitPrice *
                      (lineItem.vatRate / 100),
                    currency?.code
                  )}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default TransactionDetail;
